import { getJwt } from '../../utils/browser';
import { fetchWebLinks } from '../requests/webLinks';
import { flattenWebLinks, normalizeWebLinkGroups } from './webLinks';

/**
 * Registre module-level de la banque de liens.
 *
 * Troisième registre de cette forme, après `variableCatalogRegistry` et
 * `imageBankRegistry`, et pour la même raison : il vit **hors** du store Puck
 * (invariant §4.3 du README), les champs de la sidebar le lisent par
 * `useSyncExternalStore`, rien ne remonte d'état dans un parent du canvas.
 *
 * Chargement **paresseux** : aucun appel à l'ouverture de l'éditeur, la banque
 * n'est lue qu'à la première ouverture d'un sélecteur de lien.
 */

const EMPTY_LINKS = [];
const EMPTY_GROUPS = [];

let source = null;
let groups = EMPTY_GROUPS;
let links = EMPTY_LINKS;
let status = 'idle';
let error = null;
let inFlight = null;
let snapshot = {
  groups: EMPTY_GROUPS, links: EMPTY_LINKS, status: 'idle', error: null,
};

const listeners = new Set();

function emit() {
  snapshot = { groups, links, status, error };
  listeners.forEach((listener) => listener());
}

/**
 * Contexte éditeur : `{ type: 'proposal' | 'web_template', id }`.
 * Ce n'est pas l'identité du document d'origine (celle de la banque d'images) —
 * les liens sont servis par le proposal lui-même, cf. `requests/webLinks.js`.
 */
export function setWebLinkSource(next) {
  const nextId = next?.id != null ? String(next.id) : null;
  const nextType = next?.type || null;
  if (source?.id === nextId && source?.type === nextType) return;
  source = nextId && nextType ? { id: nextId, type: nextType } : null;
  groups = EMPTY_GROUPS;
  links = EMPTY_LINKS;
  status = 'idle';
  error = null;
  inFlight = null;
  emit();
}

export function getWebLinkSource() {
  return source;
}

export function subscribeWebLinks(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getWebLinksSnapshot() {
  return snapshot;
}

/**
 * Charge la banque (une seule requête en vol à la fois).
 *
 * L'échec reste **visible** : `status: 'error'`. Une banque vide et une banque
 * illisible ne se disent pas pareil à l'utilisateur, et tant que le back n'a
 * pas livré l'endpoint c'est une 404 qu'on reçoit — le sélecteur doit pouvoir
 * le dire au lieu d'annoncer « aucun lien ».
 */
export function loadWebLinks({ force = false } = {}) {
  if (!source) {
    if (status !== 'unavailable') {
      status = 'unavailable';
      error = null;
      emit();
    }
    return Promise.resolve(EMPTY_LINKS);
  }
  if (!force && status === 'ready') return Promise.resolve(links);
  if (inFlight && !force) return inFlight;

  const requested = source;
  status = 'loading';
  error = null;
  emit();

  inFlight = fetchWebLinks(requested)
    .then((raw) => {
      // Requête d'un document précédent : son résultat ne doit pas s'installer.
      if (requested !== source) return links;
      // Le groupe des liens personnels porte le nom de son propriétaire : c'est
      // ici qu'on sait qui regarde, et donc lequel s'appelle « Mes liens ».
      groups = normalizeWebLinkGroups(raw, getJwt()?.id);
      links = flattenWebLinks(groups);
      status = 'ready';
      error = null;
      emit();
      return links;
    })
    .catch((e) => {
      if (requested !== source) return links;
      console.error('[webviewer] chargement de la banque de liens', requested, e);
      status = 'error';
      error = e;
      emit();
      return EMPTY_LINKS;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** Remise à zéro complète — réservée aux tests. */
export function resetWebLinkRegistry() {
  source = null;
  groups = EMPTY_GROUPS;
  links = EMPTY_LINKS;
  status = 'idle';
  error = null;
  inFlight = null;
  snapshot = {
    groups: EMPTY_GROUPS, links: EMPTY_LINKS, status: 'idle', error: null,
  };
  listeners.clear();
}
